import booleanIntersect from "@turf/boolean-intersects";
import { lineString, points, pointsWithinPolygon, polygon } from "@turf/turf";
import { SeriesOption } from "echarts";
import { LassoSelectionParams } from "types/lasso";

/**
 * Filters scatter items based on lasso selection.
 * @param params - The lasso selection parameters.
 * @param series - Array of series options.
 * @param uwiList - An array of items (one per datum); adjust type as needed.
 * @returns An array of filtered items.
 */
export function getScatterFilteredItems(
  params: LassoSelectionParams,
  series: SeriesOption[],
  uwiList = []
) {
  const selected = params.batch[0]?.selected;
  const filtered = selected?.filter((s) => s.dataIndex.length);

  const list = [];
  if (!filtered?.length) {
    return [];
  }

  // filter brushed wells
  filtered.forEach((item) => {
    const { seriesIndex, dataIndex } = item;
    const rawSeriesData = series[seriesIndex]?.data;

    if (!rawSeriesData) {
      // eslint-disable-next-line no-console
      console.warn("No series data found for seriesIndex: ", seriesIndex);
      return;
    }

    const isNested = Array.isArray(rawSeriesData[0]);
    const seriesData = isNested ? (rawSeriesData as unknown[]).flat() : rawSeriesData;

    // get uwi list for every dataIndex
    const uwis = dataIndex.map((index) => {
      const seriesDataIndex = (index + 1) * 3 - 1;
      const uwiIndex = seriesData[seriesDataIndex];
      return uwiList[uwiIndex];
    });

    list.push(...uwis);
  });

  return list;
}

export function getLineChartFilteredItems(
  params: LassoSelectionParams,
  series,
  isDateChart?: boolean
) {
  const list = [];
  const lassoCoordinates = params.batch[0]?.areas[0]?.coordRange;
  if (lassoCoordinates) {
    const firstCoordinate = lassoCoordinates[0];
    lassoCoordinates.push(firstCoordinate);
    const lassoPolygon = polygon([lassoCoordinates], { name: "lasso" });
    series.forEach((s) => {
      if (!s.data) return;
      let data;
      if (isDateChart) {
        data = s.data.map((dataPoint) => {
          const timestamp = new Date(dataPoint[0]).getTime();
          return [timestamp, dataPoint[1]];
        });
      } else {
        data = s.data.map((list) => list.slice(0, 2));
      }

      if (data.length < 2) return [];
      const line = lineString(data, { name: "line" });
      const intersects = booleanIntersect(line, lassoPolygon);
      if (!intersects) return;

      const seriesName = s.name;
      if (seriesName.includes("Forecast")) {
        const resultString = seriesName.split(" Forecast")[0];
        list.push(resultString, resultString);
      } else {
        list.push(s.name);
      }
    });
  }

  return list;
}

export function getStackedBarChartFilteredItems(params, series) {
  const selected = params.batch[0]?.selected;
  const filtered = selected?.filter((s) => s.dataIndex.length);

  const filteredSeriesList = [];

  if (!filtered?.length) {
    return filteredSeriesList;
  }

  filtered.forEach((item) => {
    const { seriesIndex, dataIndex } = item;
    const seriesData = series[seriesIndex]?.data;

    if (!seriesData) {
      return;
    }
    dataIndex.forEach((index) => {
      if (seriesData[index] > 0) {
        filteredSeriesList.push(series[seriesIndex].name);
      }
    });
  });
  return filteredSeriesList;
}

export function getBoxPlotFilteredItems(params, series, instance, isJitterOn) {
  const list = [];
  const lassoCoordinates = params.batch[0]?.areas[0]?.range;
  if (!lassoCoordinates) {
    return list;
  }
  const firstCoordinate = lassoCoordinates[0];
  lassoCoordinates.push(firstCoordinate);
  const lassoPolygon = polygon([lassoCoordinates]);
  const allPoints = [];
  const pointToUwiMap = new Map();

  // select individual points if jitter plot is enabled
  if (isJitterOn) {
    Object.keys(instance._chartsMap).forEach((key, seriesIndex) => {
      const jitterSeries = instance._chartsMap[key];
      if (!jitterSeries.group?._children || !key.includes("custom")) return;

      jitterSeries.group._children.forEach((dataPoint, pointIndex) => {
        const circleShape = dataPoint.shape;

        if (circleShape?.cx && circleShape?.cy) {
          const coordinates = [circleShape.cx, circleShape.cy];
          allPoints.push(coordinates);
          const coordinatesKey = JSON.stringify(coordinates);

          const uwi = series[seriesIndex]?.data?.[pointIndex]?.[3];
          if (uwi) {
            pointToUwiMap.set(coordinatesKey, uwi);
          }
        }
      });
    });

    const pointsFeatureCollection = points(allPoints);
    const pointsWithin = pointsWithinPolygon(pointsFeatureCollection, lassoPolygon);
    pointsWithin.features.forEach((feature) => {
      const coordinatesKey = JSON.stringify(feature.geometry.coordinates);
      const uwi = pointToUwiMap.get(coordinatesKey);
      if (uwi) {
        list.push(uwi);
      }
    });
  } else {
    const boxPlotSeries = instance._chartsMap["_ec_box_series.boxplot"]?.group?._children;
    if (!boxPlotSeries) {
      return list;
    }

    for (let i = 0; i < boxPlotSeries.length; i++) {
      const shape = boxPlotSeries[i].shape;
      if (!shape.points) {
        continue;
      }

      const boxPlotPoints = points(shape.points);
      const result = pointsWithinPolygon(boxPlotPoints, lassoPolygon);

      let seriesIndex = i;
      for (const prop in boxPlotSeries[i]) {
        if (
          Object.prototype.hasOwnProperty.call(boxPlotSeries[i], prop) &&
          typeof boxPlotSeries[i][prop] === "object"
        ) {
          const obj = boxPlotSeries[i][prop];
          if (obj && obj.dataIndex) {
            seriesIndex = obj.dataIndex;
          }
        }
      }

      if (result.features.length > 0) {
        list.push(series[0].data[seriesIndex]?.name);
      }
    }
  }
  return list;
}
